import { FC, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { ExportDetails } from "../../../types/niffler";
import { ExportMetrics } from "./ExportMetrics";
import { ExportTable } from "./ExportTable";
import { ExportDetailsModal } from "./ExportDetailsModal";
import { ExportHistoryError } from "./components/ExportHistoryError";
import { ExportHistoryEmpty } from "./components/ExportHistoryEmpty";
import { useExportHistory } from "./hooks/useExportHistory";
import { useExportMetrics } from "./hooks/useExportMetrics";
import { lightScrollbarStyle } from "../../../styles/lightScrollbarStyle";
import { EXPORT_STATUS } from "./constants";

export const ExportsView: FC = () => {
  const [selectedExport, setSelectedExport] = useState<ExportDetails | null>(
    null
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<EXPORT_STATUS | undefined>(
    undefined
  );

  // Centralized data fetching
  const historyData = useExportHistory({ statusFilter });
  const metricsData = useExportMetrics();

  // Unified states
  const hasError = historyData.hasError || metricsData.hasError;
  const isLoading = historyData.loading || metricsData.loading;
  const isEmpty =
    !isLoading &&
    (historyData.totalCount === 0 ||
      (metricsData.stats?.totalExports ?? 0) === 0);

  // Unified refetch with useCallback for performance
  const handleRefetch = useCallback(async () => {
    await Promise.allSettled([historyData.refetch(), metricsData.refetch()]);
  }, [historyData, metricsData]);

  const handleViewDetails = useCallback((exportDetails: ExportDetails) => {
    setSelectedExport(exportDetails);
    setModalOpen(true);
  }, []); // No dependencies, so it's created only once

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedExport(null);
  }, []); // No dependencies, so it's created only once

  // Unified error state
  if (hasError) {
    return <ExportHistoryError onRetry={handleRefetch} />;
  }

  // Unified empty state
  if (isEmpty) {
    return <ExportHistoryEmpty />;
  }

  return (
    <Stack
      direction="column"
      spacing={3}
      overflow="auto"
      paddingRight={1}
      height="100%"
      sx={{ ...lightScrollbarStyle }}
    >
      {/* Metrics Section */}
      <ExportMetrics stats={metricsData.stats} loading={metricsData.loading} />

      {/* Table Section */}
      <Box flexGrow={1}>
        <ExportTable
          historyData={historyData}
          onViewDetails={handleViewDetails}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
        />
      </Box>

      {/* Details Modal */}
      <ExportDetailsModal
        exportDetails={selectedExport}
        open={modalOpen}
        onClose={handleCloseModal}
      />
    </Stack>
  );
};
